import { useMemo } from 'react';

import { useIndirectTaxTransactionAggregates } from '@commenda/api/indirect-tax/transactions';
import { TIndirectTaxTransactionAggregates } from '@commenda/api/indirect-tax/transactions/types';

import { IndirectTaxRegion } from '../../common';
import { buildIndirectTaxTransactionApiFilters } from '../../transactions/dashboard/buildTransactionApiFilters';
import { IndirectTaxFilingsHelper } from '../helpers';
import { IIndirectTaxFilingDetail } from '../types';

interface UseIndirectTaxFilingAggregatesArgs {
  filing: IIndirectTaxFilingDetail;
  region: IndirectTaxRegion;
  corporationId: number;
}

// Transaction aggregates (counts, gross + subtotal sales/purchases, output/input
// VAT) for a filing's period, computed from the SAME filing-scoped filters the
// "View transactions" modal and the transaction-count metric use. Totals are
// converted into the region's default currency (EUR for EU, GBP for UK) by the
// aggregate endpoint, and arrive in major units. Used to back the VAT Return
// summary panel.
export function useIndirectTaxFilingAggregates({
  filing,
  region,
  corporationId,
}: UseIndirectTaxFilingAggregatesArgs): {
  aggregates: TIndirectTaxTransactionAggregates;
  isLoading: boolean;
} {
  const apiFilters = useMemo(
    () =>
      buildIndirectTaxTransactionApiFilters({
        corporationId,
        region,
        filters: IndirectTaxFilingsHelper.buildTransactionFiltersForFiling(
          filing,
          region
        ),
      }),
    [corporationId, region, filing]
  );

  const targetCurrency = filing.currency;

  const { aggregates, isLoading } = useIndirectTaxTransactionAggregates({
    filters: { ...apiFilters, target_currency: targetCurrency },
    enabled: Boolean(targetCurrency),
  });

  return { aggregates, isLoading };
}
